import React, { Profiler, ProfilerOnRenderCallback, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";

import { useAuth } from "@context/AuthProvider";
import { Session } from "@/lib/curio";
import { DEFAULT_OPEN_MODE, MenuNode } from "@/routes";
import { useTabs } from "@context/TabsContext";
import { useHookMutation } from "../../../hooks/useHookMutation";
import { styles } from "./Main.styles";

const noopAction = async () => undefined;

/* eslint-disable no-console */
const onRender: ProfilerOnRenderCallback = (id, phase, actualDuration, baseDuration, startTime, commitTime) => {
  if (import.meta.env.DEV && import.meta.env.MOSTRAR_LOGS) {
    console.log(`Rendered ${id} in ${actualDuration.toFixed(2)}ms (${phase})`);
    console.log(`Base duration: ${baseDuration.toFixed(2)}ms`);
    console.log(`Started at: ${startTime.toFixed(2)}ms, committed at: ${commitTime.toFixed(2)}ms\n\n`);
  }
};
/* eslint-enable no-console */

interface MenuTreeItemProps {
  node: MenuNode;
  depth: number;
}

export const MenuTreeItem: React.FC<MenuTreeItemProps> = ({ node, depth }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { session } = useAuth();
  const { openTab, setActiveTab } = useTabs();
  const [open, setOpen] = useState(false);
  const { mutateAsync: executeAction } = useHookMutation<void, Session>(node.action ?? noopAction);
  const Icon = node.icon;
  const pl = (depth + 1) * 2;

  if (node.children) {
    return (
      <>
        <ListItemButton sx={styles.menuItemPadding(pl)} onClick={() => setOpen((prev) => !prev)}>
          {Icon && (
            <ListItemIcon>
              <Icon />
            </ListItemIcon>
          )}
          <ListItemText primary={node.label} />
          {open ? <ExpandLess /> : <ExpandMore />}
        </ListItemButton>
        <Collapse in={open} unmountOnExit>
          <List disablePadding>
            {node.children.map((child) => (
              <MenuTreeItem key={child.label} node={child} depth={depth + 1} />
            ))}
          </List>
        </Collapse>
      </>
    );
  }

  // Precedência: action > mode. DASHBOARD_NODE (mode: "route") cobre o caso antes tratado por
  // comparação de string (`node.path === "dashboard"`).
  const handleClick = async () => {
    if (node.action && session) {
      await executeAction(session);
      return;
    }
    const mode = node.mode ?? DEFAULT_OPEN_MODE;
    if (mode === "route" && node.path) {
      setActiveTab(null);
      navigate(`/${node.path}`);
      return;
    }
    openTab(node);
  };

  return (
    <Profiler id={node.label} onRender={onRender}>
      <ListItemButton
        sx={styles.menuItemPadding(pl)}
        selected={location.pathname === `/${node.path}`}
        onClick={handleClick}>
        {Icon && (
          <ListItemIcon>
            <Icon />
          </ListItemIcon>
        )}
        <ListItemText primary={node.label} />
      </ListItemButton>
    </Profiler>
  );
};
